import React, { forwardRef, type Ref } from 'react';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { MenuLinkItem, type MenuLinkItemProps } from '@atlassian/navigation-system';

type MenuLinkItemWithAnalyticsProps<RouterLinkConfig extends Record<string, unknown> = never> =
	MenuLinkItemProps<RouterLinkConfig> & {
		analytics?: {
			actionSubjectId: string;
			itemId?: string;
			[attribute: string]: boolean | number | string | undefined;
		};
	};

function MenuLinkItemWithAnalytics<RouterLinkConfig extends Record<string, unknown> = never>(
	{ analytics, onClick, ...rest }: MenuLinkItemWithAnalyticsProps<RouterLinkConfig>,
	ref: Ref<HTMLAnchorElement>,
) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick: MenuLinkItemWithAnalyticsProps['onClick'] = (event, analyticsEvent) => {
		onClick?.(event, analyticsEvent);

		if (!analytics) {
			return;
		}

		const { actionSubjectId, ...analyticsAttributes } = analytics;

		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'navigationItem',
			}),
			actionSubjectId,
			analyticsAttributes,
		);
	};

	return <MenuLinkItem<RouterLinkConfig> ref={ref} onClick={handleClick} {...rest} />;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export default forwardRef(MenuLinkItemWithAnalytics) as <
	RouterLinkConfig extends Record<string, unknown> = never,
>(
	props: MenuLinkItemWithAnalyticsProps<RouterLinkConfig> & {
		ref?: Ref<HTMLAnchorElement>;
	},
) => ReturnType<typeof MenuLinkItem>;
